import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  BarChart3,
  Building,
  ChevronDown,
  CreditCard,
  FileText,
  LayoutDashboard,
  Lock,
  LogOut,
  Plus,
  Settings,
  Users,
  MapPin,
  Truck,
  Gauge,
  Download,
  Cpu,
  Brain,
  Bot,
  Zap,
  Link,
  TrendingUp,
  Code,
  Database,
  HeadphonesIcon,
  ListTodo,
  Activity,
  GitBranch,
  Timer,
  Tag,
  TestTube,
  Upload,
  CheckCircle,
  AlertCircle,
  Edit,
  CheckCheck,
  Bell,
  Sparkles,
  ChartPie,
  Leaf,
  ArrowRight,
  Package
} from "lucide-react";
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { useAccessResolver } from '@/contexts/AccessResolverContext';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Card } from "@/components/ui/card";

const navigationItems = (workspaceId: string, workspaceSlug: string) => [
  {
    title: "Bill Collection & Queue",
    moduleKey: "bill_collection", // Module key for section-level filtering
    items: [
      { title: "Bill Queue", url: `/w/${workspaceId}/dashboard/bill-queue`, icon: ListTodo, permission: "bill_collection" },
      { title: "Bills", url: `/w/${workspaceId}/dashboard/bills`, icon: FileText, permission: "bill_collection" },
      { title: "Collection Dashboard", url: `/w/${workspaceId}/dashboard/collection`, icon: BarChart3, permission: "bill_collection" },
      { title: "Collection Activities", url: `/w/${workspaceId}/collection-activities`, icon: Activity, permission: "bill_collection" },
      { title: "Collection Workflows", url: `/w/${workspaceId}/collection-workflows`, icon: GitBranch, permission: "bill_collection" },
    ]
  },
  {
    title: "Bill Processing",
    moduleKey: "bill_processing",
    items: [
      { title: "Dashboard", url: `/w/${workspaceId}/dashboard`, icon: LayoutDashboard },
      { title: "Processing", url: `/w/${workspaceId}/dashboard/processing`, icon: Cpu, permission: "bill_processing" },
    ]
  },
  {
    title: "Sandbox",
    moduleKey: undefined, // No module key - show if user has any relevant permissions
    items: [
      { title: "Parser Sandbox", url: `/w/${workspaceId}/dashboard/parser-sandbox`, icon: TestTube, permission: "bill_processing" },
      { title: "Collector Sandbox", url: `/w/${workspaceId}/dashboard/collector-sandbox`, icon: TestTube, permission: "bill_collection" },
    ]
  },
  {
    title: "Customer Management",
    moduleKey: "onboarding",
    items: [
      { title: "Partners", url: `/w/${workspaceId}/dashboard/partners`, icon: Building, permission: "onboarding" },
      { title: "Customers", url: `/w/${workspaceId}/dashboard/customers`, icon: Users, permission: "onboarding" },
      { title: "Sites", url: `/w/${workspaceId}/dashboard/sites`, icon: MapPin, permission: "onboarding" },
      { title: "Suppliers", url: `/w/${workspaceId}/dashboard/suppliers`, icon: Truck, permission: "onboarding" },
      { title: "Accounts", url: `/w/${workspaceId}/dashboard/accounts`, icon: CreditCard, permission: "onboarding" },
      { title: "Entities", url: `/w/${workspaceId}/dashboard/entities`, icon: Tag, permission: "onboarding" },
      { title: "Meters", url: `/w/${workspaceId}/dashboard/meters`, icon: Gauge, permission: "onboarding" },
    ]
  },
  {
    title: "Bill Collection",
    moduleKey: "bill_collection",
    items: [
      { title: "Collection Dashboard", url: `/w/${workspaceId}/dashboard/collection`, icon: BarChart3, permission: "bill_collection" },
      { title: "Manual Upload", url: `/w/${workspaceId}/dashboard/bills/upload`, icon: Upload, permission: "bill_collection" },
      { title: "Bill Queue", url: `/w/${workspaceId}/dashboard/bill-queue`, icon: ListTodo, permission: "bill_collection" },
      { title: "Bills", url: `/w/${workspaceId}/dashboard/bills`, icon: FileText, permission: "bill_collection" },
    ]
  },
        {
          title: "Parser Workflows",
          moduleKey: "bill_processing",
          items: [
            { title: "Review Parsed Bills", url: `/w/${workspaceId}/dashboard/bills/review`, icon: CheckCircle, permission: "bill_processing" },
            { title: "Unparsed Bills", url: `/w/${workspaceId}/dashboard/bills/unparsed`, icon: AlertCircle, permission: "bill_processing" },
            { title: "Manual Parser Queue", url: `/w/${workspaceId}/dashboard/parser/manual-queue`, icon: Edit, permission: "bill_processing" },
            { title: "Dev Parser Queue", url: `/w/${workspaceId}/dashboard/parser/dev`, icon: Code, permission: "bill_processing" },
            { title: "Valid Bills", url: `/w/${workspaceId}/dashboard/bills/valid`, icon: CheckCheck, permission: "bill_processing" },
          ]
        },
  {
    title: "AI & Automation",
    moduleKey: undefined, // Show if user has any of: ai_parser, ai_agents, automation
    items: [
      { title: "AI Parser", url: `/w/${workspaceId}/dashboard/ai-parser`, icon: Brain, permission: "ai_parser" },
      { title: "AI Agents", url: `/w/${workspaceId}/dashboard/ai-agents`, icon: Bot, permission: "ai_agents" },
      { title: "Automation", url: `/w/${workspaceId}/dashboard/automation`, icon: Zap, permission: "automation" },
    ]
  },
  {
    title: "Reports & Analytics",
    moduleKey: undefined, // Show if user has any of: reporting, analytics, enterprise_analytics, compliance
    items: [
      { title: "Reports", url: `/w/${workspaceId}/dashboard/reports`, icon: BarChart3, permission: "reporting" },
      { title: "Analytics", url: `/w/${workspaceId}/dashboard/analytics`, icon: TrendingUp, permission: "analytics" },
      { title: "Heat Map", url: `/w/${workspaceId}/dashboard/analytics/heatmap`, icon: TrendingUp, permission: "analytics" },
      { title: "Enterprise Analytics", url: `/w/${workspaceId}/dashboard/enterprise-analytics`, icon: TrendingUp, permission: "enterprise_analytics" },
      { title: "Compliance", url: `/w/${workspaceId}/dashboard/compliance`, icon: Lock, permission: "compliance" },
    ]
  },
  {
    title: "Tenant Utility Manager",
    moduleKey: "tum",
    items: [
      { title: "Dashboard", url: `/w/${workspaceId}/dashboard/tum`, icon: LayoutDashboard, permission: "tum" },
      { title: "Onboarding", url: `/w/${workspaceId}/dashboard/tum/onboarding`, icon: Upload, permission: "tum" },
      { title: "Bill Journey", url: `/w/${workspaceId}/dashboard/tum/bill-journey`, icon: GitBranch, permission: "tum" },
      { 
        title: "Properties", 
        url: `/w/${workspaceId}/dashboard/tum/properties`, 
        icon: Building, 
        permission: "tum",
        subItems: [
          { title: "Properties", url: `/w/${workspaceId}/dashboard/tum/properties`, icon: Building },
          { title: "Tenants", url: `/w/${workspaceId}/dashboard/tum/properties/tenants`, icon: Users },
          { title: "Vendors", url: `/w/${workspaceId}/dashboard/tum/properties/vendors`, icon: Truck },
          { title: "Bills & Accounts", url: `/w/${workspaceId}/dashboard/tum/properties/bills`, icon: FileText },
        ]
      },
      { title: "Payment Report", url: `/w/${workspaceId}/dashboard/tum/payment-report`, icon: CreditCard, permission: "tum" },
      { 
        title: "Reports & Analytics", 
        url: `/w/${workspaceId}/dashboard/tum/reports`, 
        icon: BarChart3, 
        permission: "tum",
        subItems: [
          { title: "Reports Library", url: `/w/${workspaceId}/dashboard/tum/reports/library`, icon: FileText, permission: "tum" },
          { title: "Portfolio", url: `/w/${workspaceId}/dashboard/tum/reports/portfolio`, icon: ChartPie, permission: "tum" },
          { title: "Sustainability", url: `/w/${workspaceId}/dashboard/tum/reports/sustainability`, icon: Leaf, permission: "tum" },
          { title: "Analytics", url: `/w/${workspaceId}/dashboard/tum/reports/analytics`, icon: TrendingUp, permission: "tum" },
        ]
      },
      { title: "Notifications", url: `/w/${workspaceId}/dashboard/tum/notifications`, icon: Bell, permission: "tum" },
      { title: "Integrations", url: `/w/${workspaceId}/dashboard/tum/integrations`, icon: Link, permission: "tum" },
    ]
  },
  {
    title: "Advanced Features",
    moduleKey: "analytics",
    items: [
      { title: "Phase 6 Dashboard", url: `/w/${workspaceId}/dashboard/phase6`, icon: Zap, permission: "analytics" },
    ]
  },
  {
    title: "Integration & API",
    moduleKey: undefined, // Show if user has any of: integrations, api
    items: [
      { title: "Integrations", url: `/w/${workspaceId}/dashboard/integrations`, icon: Link, permission: "integrations" },
      { title: "API Access", url: `/w/${workspaceId}/dashboard/api`, icon: Code, permission: "api" },
    ]
  },
  {
    title: "Storage & Support",
    moduleKey: undefined, // Show if user has any of: storage, support
    items: [
      { title: "Enhanced Storage", url: `/w/${workspaceId}/dashboard/storage`, icon: Database, permission: "storage" },
      { title: "Priority Support", url: `/w/${workspaceId}/dashboard/support`, icon: HeadphonesIcon, permission: "support" },
    ]
  },
  {
    title: "Workspace Admin",
    moduleKey: "workspace_admin",
    items: [
      { title: "Dashboard", url: `/${workspaceSlug}/admin/dashboard`, icon: LayoutDashboard, permission: "workspace_admin" },
      { title: "User Management", url: `/${workspaceSlug}/admin/users`, icon: Users, permission: "workspace_admin" },
      { title: "Role Management", url: `/${workspaceSlug}/admin/roles`, icon: Users, permission: "workspace_admin" },
      { title: "Module Management", url: `/${workspaceSlug}/admin/modules`, icon: Package, permission: "workspace_admin" },
      { title: "Trial Management", url: `/${workspaceSlug}/admin/trial`, icon: Timer, permission: "trial_management" },
      { title: "Workspace Settings", url: `/${workspaceSlug}/admin/settings`, icon: Settings, permission: "workspace_admin" },
      { title: "Branding", url: `/${workspaceSlug}/admin/branding`, icon: Sparkles, permission: "workspace_admin" },
      { title: "Audit Logs", url: `/${workspaceSlug}/admin/audit-logs`, icon: FileText, permission: "workspace_admin" },
    ]
  }
];

export function AppSidebar() {
  // ============================================
  // HOOKS - MUST BE CALLED UNCONDITIONALLY
  // ============================================
  const location = useLocation();
  const navigate = useNavigate();
  const sidebar = useSidebar();
  const collapsed = sidebar.state === 'collapsed';
  const { currentWorkspace, contextStatus, isLoading: workspaceLoading, hasPermissionSync, workspaceRole: contextWorkspaceRole, isAdmin: contextIsAdmin, isOwner: contextIsOwner } = useWorkspace();
  const { user, logout } = useAuth();
  const { plan } = useAccessResolver();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id || '');
  
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openSubItems, setOpenSubItems] = useState<Record<string, boolean>>({});

  // ============================================
  // VISIBILITY FLAGS - CALCULATED AFTER HOOKS
  // ============================================
  
  // Admin access check (matches AdminGuard logic)
  // Allows access if:
  // - user.role === 'super_admin'
  // - OR isOwner === true
  // - OR workspaceRole === 'admin' or 'workspace_admin'
  const isSuperAdmin = user?.role === 'super_admin';
  const isOwner = contextIsOwner || moduleAccess.isOwner; // Prefer WorkspaceContext, fallback to moduleAccess
  // Use workspaceRole from WorkspaceContext (always available, defaults to null, never undefined)
  // Fallback to moduleAccess.userRole for backward compatibility
  const workspaceRole = contextWorkspaceRole ?? moduleAccess.userRole;
  // Use WorkspaceContext values first, then fallback to moduleAccess
  const isWorkspaceAdmin = 
    contextIsAdmin ||
    workspaceRole === 'admin' || 
    workspaceRole === 'workspace_admin' ||
    user?.role === 'workspace_admin' || 
    user?.role === 'admin';
  
  const canAccessAdmin = isSuperAdmin || isOwner || isWorkspaceAdmin;
  const canBypass = isSuperAdmin || isOwner; // Owners and super_admin bypass all checks

  // Check if current workspace is a trial
  const isTrialWorkspace = plan?.is_trial || false;
  
  // Trial mode: limit modules to specific ones
  const TRIAL_MODULES = ['bill_processing', 'onboarding', 'reporting'];
  
  // Helper to check if module is accessible (respects trial mode)
  // Uses useModuleAccess hook for module checks
  const canAccessModule = (moduleKey: string | undefined): boolean => {
    // Owners and super_admin see everything
    if (canBypass) {
      return true;
    }
    
    // If no module key, show by default
    if (!moduleKey) {
      return true;
    }
    
    // Trial users only see trial modules
    if (isTrialWorkspace && !TRIAL_MODULES.includes(moduleKey)) {
      return false;
    }
    
    // Check if module is enabled using useModuleAccess
    return moduleAccess.hasModule(moduleKey);
  };
  
  // Helper to check if permission is accessible
  // Uses useModuleAccess hook for module checks
  const canAccessPermission = (permission: string | undefined): boolean => {
    // If no permission required, show by default
    if (!permission) {
      return true;
    }
    
    // Owners and super_admin see everything
    if (canBypass) {
      return true;
    }
    
    // Special handling for workspace_admin permission
    // This is a role-based permission, not a module
    // Should be visible to workspace owners/admins (from WorkspaceContext)
    if (permission === 'workspace_admin') {
      // Use workspaceRole from WorkspaceContext (always available, defaults to null)
      // Also check computed flags from WorkspaceContext
      return contextIsOwner || 
             contextIsAdmin || 
             workspaceRole === 'owner' || 
             workspaceRole === 'admin' || 
             canAccessAdmin;
    }
    
    // Trial users only see trial modules
    if (isTrialWorkspace && !TRIAL_MODULES.includes(permission)) {
      return false;
    }
    
    // Check if module is enabled using useModuleAccess
    return moduleAccess.hasModule(permission);
  };

  // ============================================
  // EARLY RETURNS - AFTER ALL HOOKS
  // ============================================
  
  // Always render sidebar for authenticated users - show skeleton while loading
  if (!user) {
    return null;
  }

  // Show skeleton sidebar while loading
  if (contextStatus === 'loading' || !currentWorkspace) {
    return (
      <Sidebar 
        className={cn(
          "border-r transition-all duration-200 ease-in-out bg-white dark:bg-background",
          collapsed ? "w-14" : "w-64"
        )}
        collapsible="icon"
      >
        <SidebarHeader className="border-b px-4 py-3 bg-white dark:bg-background">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-8 w-8 rounded" />
            {!collapsed && (
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            )}
          </div>
        </SidebarHeader>
        <SidebarContent className="px-2 bg-white dark:bg-background">
          <div className="p-2">
            <Skeleton className="h-8 w-full" />
          </div>
          <div className="space-y-2">
            {[1,2,3].map(i => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <div className="space-y-1 ml-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                </div>
              </div>
            ))}
          </div>
        </SidebarContent>
      </Sidebar>
    );
  }

  const workspaceId = currentWorkspace?.id || '';
  const workspaceSlug = currentWorkspace?.slug || '';
  
  // Filter navigation items based on module entitlements
  // Uses useModuleAccess hook for all module checks
  // Owners and super_admin see everything
  // Trial users see only trial modules
  const filteredNavigationItems = navigationItems(workspaceId, workspaceSlug).filter((group) => {
    // Workspace Admin section: ALWAYS visible for admin/owner (bypasses module checks)
    if (group.title === 'Workspace Admin') {
      return canAccessAdmin; // Always show for admin/owner
    }
    
    // If group has a moduleKey, check if user has access to that module
    if (group.moduleKey) {
      return canAccessModule(group.moduleKey);
    }
    
    // For groups without a moduleKey, check if any item in the group is accessible
    // This handles groups like "Sandbox", "AI & Automation", etc. that span multiple modules
    if (group.items && group.items.length > 0) {
      return group.items.some((item) => {
        return canAccessPermission(item.permission);
      });
    }
    
    // Default: show the group if no module restrictions
    return true;
  });
  
  // Only log debug info in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🎯 [AppSidebar] Navigation debug:', {
      workspaceId,
      currentWorkspace: currentWorkspace?.name,
      isOwner: moduleAccess.isOwner,
      userRole: user?.role,
      workspaceRole,
      canAccessAdmin,
      totalNavigationItems: filteredNavigationItems.reduce((total, group) => total + group.items.length, 0),
      totalGroups: filteredNavigationItems.length,
      moduleAccessData: {
        enabledModulesCount: moduleAccess.enabledModules?.length || 0,
        enabledModules: moduleAccess.enabledModules,
        isLoading: moduleAccess.isLoading
      },
      allPermissionsInNavigation: filteredNavigationItems
        .flatMap(group => group.items)
        .map(item => item.permission)
        .filter(Boolean)
    });
  }

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const getNavCls = (
    path: string, 
    isEnabled: boolean
  ) => {
    const baseClasses = "group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium transition-colors";
    
    if (!isEnabled) {
      return `${baseClasses} text-muted-foreground cursor-pointer opacity-60 hover:opacity-80`;
    }
    
    if (isActive(path)) {
      return `${baseClasses} bg-primary text-primary-foreground hover:bg-primary/90`;
    }
    
    return `${baseClasses} text-foreground hover:bg-muted hover:text-foreground`;
  };

  const toggleGroup = (groupTitle: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  const toggleSubItem = (itemTitle: string) => {
    setOpenSubItems(prev => ({
      ...prev,
      [itemTitle]: !prev[itemTitle]
    }));
  };

  const hasPermission = (moduleId?: string): boolean => {
    if (!moduleId) return true; // No permission required
    
    // CRITICAL: Owner and super_admin bypass first  
    if (canBypass) {
      return true;
    }
    
    // Check if module is enabled using useModuleAccess
    return moduleAccess.hasModule(moduleId);
  };

  const handleNavigation = (url: string, moduleId?: string) => {
    if (moduleId && !hasPermission(moduleId)) {
      // Could show access request dialog here
      console.warn('Navigation blocked - insufficient permissions for:', moduleId);
      return;
    }
    // Handle query parameters in URL
    const [path, query] = url.split('?');
    if (query) {
      const params = new URLSearchParams(query);
      navigate({ pathname: path, search: params.toString() });
    } else {
      navigate(url);
    }
  };

  const handleLogout = async () => {
    try {
      logout();
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Sidebar 
      className={cn(
        "border-r transition-all duration-200 ease-in-out bg-white dark:bg-background",
        collapsed ? "w-14" : "w-64"
      )}
      collapsible="icon"
    >
      {/* Header */}
      <SidebarHeader className="border-b px-4 py-3 bg-white dark:bg-background">
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            <img 
              src="/brand/billportal-icon.svg" 
              alt="BillPortal" 
              className="h-8 w-8 rounded"
              onError={(e) => {
                // Fallback to original if image fails to load
                e.currentTarget.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.className = 'h-8 w-8 rounded bg-primary flex items-center justify-center';
                fallback.innerHTML = '<span class="text-sm font-bold text-primary-foreground">B</span>';
                e.currentTarget.parentNode.appendChild(fallback);
              }}
            />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold truncate">BillPortal</h2>
              <p className="text-xs text-muted-foreground truncate">
                {currentWorkspace?.name}
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 bg-white dark:bg-background">
        {/* Quick Action Button */}
        {!collapsed && (
          <div className="p-2">
            <Button 
              variant="hero" 
              size="sm" 
              className="w-full justify-start"
              onClick={() => navigate(`/w/${workspaceId}/dashboard/upload`)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Bill
            </Button>
          </div>
        )}

        {/* Navigation Groups */}
        <div className="space-y-1">
          {filteredNavigationItems.map((group) => {
            return (
              <SidebarGroup key={group.title}>
                <Collapsible
                  open={openGroups[group.title] ?? true}
                  onOpenChange={() => toggleGroup(group.title)}
                >
                  <SidebarGroupLabel asChild>
                    <CollapsibleTrigger className="group/collapsible w-full">
                      <div className="flex items-center w-full">
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          {group.title}
                        </span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=closed]/collapsible:rotate-180" />
                      </div>
                    </CollapsibleTrigger>
                  </SidebarGroupLabel>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {group.items.map((item) => {
                          const isEnabled = hasPermission(item.permission || '');
                          const hasSubItems = item.subItems && item.subItems.length > 0;
                          const isSubItemOpen = openSubItems[item.title] ?? false;
                          const isItemActive = isActive(item.url) || (hasSubItems && item.subItems?.some(sub => isActive(sub.url)));
                          
                          return (
                            <SidebarMenuItem key={item.title}>
                              {hasSubItems ? (
                                <Collapsible open={isSubItemOpen} onOpenChange={() => toggleSubItem(item.title)}>
                                  <CollapsibleTrigger asChild>
                                    <SidebarMenuButton 
                                      className={cn(getNavCls(item.url, isEnabled), isItemActive && "bg-sidebar-accent")}
                                      disabled={!isEnabled && !moduleAccess.isOwner}
                                    >
                                      <item.icon className="h-4 w-4" />
                                      <span>{item.title}</span>
                                      <ChevronDown className={cn(
                                        "ml-auto h-4 w-4 transition-transform",
                                        isSubItemOpen && "rotate-180"
                                      )} />
                                      {!isEnabled && !moduleAccess.isOwner && <Lock className="h-3 w-3 ml-1" />}
                                    </SidebarMenuButton>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <SidebarMenuSub>
                                      {item.subItems?.map((subItem) => {
                                        const isSubEnabled = hasPermission(subItem.permission || '');
                                        return (
                                          <SidebarMenuSubItem key={subItem.title}>
                                            <SidebarMenuSubButton
                                              asChild
                                              isActive={isActive(subItem.url)}
                                            >
                                              <NavLink
                                                to={subItem.url}
                                                className={({ isActive }) => cn(
                                                  "flex items-center gap-2",
                                                  isActive && "bg-sidebar-accent"
                                                )}
                                                onClick={(e) => {
                                                  if (!isSubEnabled && !moduleAccess.isOwner) {
                                                    e.preventDefault();
                                                  }
                                                }}
                                              >
                                                <subItem.icon className="h-4 w-4" />
                                                <span>{subItem.title}</span>
                                              </NavLink>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>
                                        );
                                      })}
                                    </SidebarMenuSub>
                                  </CollapsibleContent>
                                </Collapsible>
                              ) : (
                              <SidebarMenuButton 
                                className={getNavCls(item.url, isEnabled)}
                                onClick={() => {
                                  if (isEnabled || moduleAccess.isOwner) {
                                    handleNavigation(item.url, item.permission);
                                  } else {
                                    console.warn('Navigation blocked - insufficient permissions for:', item.permission);
                                  }
                                }}
                                disabled={!isEnabled && !moduleAccess.isOwner}
                              >
                                <item.icon className="h-4 w-4" />
                                <span>{item.title}</span>
                                {!isEnabled && !moduleAccess.isOwner && <Lock className="h-3 w-3 ml-auto" />}
                              </SidebarMenuButton>
                              )}
                            </SidebarMenuItem>
                          );
                        })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarGroup>
            );
          })}
        </div>

        {/* Collapsed Navigation */}
        {collapsed && (
          <div className="space-y-1 mt-4">
            {filteredNavigationItems.map((group) => {
              return group.items.map((item) => {
                const isEnabled = hasPermission(item.permission || '');
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      size="sm"
                      className={getNavCls(item.url, isEnabled)}
                      onClick={() => handleNavigation(item.url, item.permission)}
                      title={`${group.title}: ${item.title}${!isEnabled ? ' (Locked)' : ''}`}
                    >
                      <item.icon className="h-4 w-4" />
                      {!isEnabled && !moduleAccess.isOwner && <Lock className="h-2 w-2 absolute -bottom-0.5 -right-0.5" />}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              });
            })}
          </div>
        )}

        {/* Trial Upgrade CTA - Show for trial workspaces */}
        {isTrialWorkspace && !collapsed && (
          <div className="p-2 mt-4">
            <Card className="p-3 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground mb-1">
                    Unlock All Features
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Upgrade to access premium modules and features
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                className="w-full mt-2 bg-primary hover:bg-primary/90"
                onClick={() => navigate(`/w/${workspaceId}/dashboard/billing`)}
              >
                Upgrade Now
                <ArrowRight className="ml-2 h-3 w-3" />
              </Button>
            </Card>
          </div>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Logout</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}